import { IllegalArgumentError, IllegalStateError } from "@core/errors/errors-core";
import { generateDeterministicId, generateDeterministicTemplateIdFromTemplateIdAndTemplateId, makeUUID } from '@core/services/orm/idGenerator';
import { BaseEntity, EntityContext } from "@core/services/orm/orm";

import { getProjectInfo, newPatchId } from "@archipad/backend/archiweb/synchroDriver";

import { BugGroupEntity } from '@archipad-models/models/BugGroupEntity';
import { MAP_FILE_TYPES, MAP_TYPES } from '@archipad-models/models/extensions/MapEntity-ext';
import { MapEntity } from '@archipad-models/models/MapEntity';
import { PhaseEntity } from '@archipad-models/models/PhaseEntity';
import { ProjectEntity } from "@archipad-models/models/ProjectEntity";

import { ProjectManager } from "@ui-archipad/backend/project/project";
import { sharedProjectList } from "@ui-archipad/backend/project/projectList";
import { voidAbortSignal, voidProgress } from "@core/tasks/asyncCaller";
import { isNotNullish } from "@core/helpers/collection.helper";
import pLimit from 'p-limit';
import { FileService } from "@core/services/file/file";
import { firstValueFrom } from 'rxjs';

import BugNoteFacadeInstance from '@archipad/services/bugNote.facade';
import { WorkflowService } from "@archipad/services/workflow.service";
import { EquivBugState, EquivBugTransition } from "@archipad-js/archipad-model";

/**
 * To use the follow commands you have to open a project on [Archipad Cloud](https://cloud.archipad.com).
 * Open the developer console. All commands are available under `Archipad.Commands`.
 * 
 * @private
 */
class Commands {
    static async getAllUserProjects(): Promise<ReadonlyArray<string>> {
        const projectList = sharedProjectList();
        
        return projectList.getProjectsIds();
    }

    /**
     * @internal
     * Retrieve the current entity context.
     */
    static async getEntityContext(): Promise<EntityContext> {
        const archipadApp = document.querySelector('archipad-app') as any;
        const projectManager = archipadApp.project as ProjectManager | null;
        if (projectManager === null) {
            throw new IllegalStateError('Cannot retrieve the project prefix outside of a project');
        }
        const projectContext = await projectManager.getContext(null).toPromise();
        return projectContext.getEntityContext();
    }

    /**
     * @internal
     * Will delete the Map entity of a given id.
     * 
     * @param mapId - The id of the Map entity to delete
     */
    static async deleteMap(mapId: string): Promise<void> {
        if (typeof mapId !== 'string') {
            throw new IllegalArgumentError(`The mapId must be a string`);
        }
    
        const entityContext = await Commands.getEntityContext();
    
        const map = entityContext.getEntity('Map', mapId);
    
        if (map === null) {
            throw new IllegalArgumentError(`Unable to find the Map<${mapId}>`);
        }
    
        entityContext.deleteEntity(map);
    
        console.log(`The Map ${mapId} has been deleted`);
    }

    /**
     * Will delete all empty FormObs maps.
     *
     * Example:
     * ```
     * > Archipad.Commands.deleteAllEmptyFormObsMaps()
     * The following map contains 1 observations and will not be deleted.
     * entityType {_entityContext: EntityContext, _attachments: {…}, _values: {…}, _updating: false}
     * Promise {<fulfilled>: "3 maps have been deleted"}
     * ```
     * @return `"<<number of deleted map>> maps have been deleted"`
     */
    static async deleteAllEmptyFormObsMaps(): Promise<string> {
        const entityContext = await Commands.getEntityContext();
        const allMaps = Object.values(entityContext.entitiesByType['Map']) as MapEntity[];
        const emptyFormObsMaps = allMaps
            .filter((map) => map.fileType === MAP_FILE_TYPES.PDF && map.type === MAP_TYPES.FORMS)
            .filter((map) => {
                if (map.bugs.length === 0) {
                    return true;
                }

                console.warn(`The following map contains ${map.bugs.length} observations and will not be deleted.`, map);
                return false;
            });

        let deleteCount = 0;

        for (const map of emptyFormObsMaps) {
            if (map.bugs.length === 0) {
                entityContext.deleteEntity(map);
                deleteCount++;
            }
        }

        return `${deleteCount} maps have been deleted`;
    }

    /**
     * @internal
     * Will output in the browser console the current project AWS S3 prefix.
     */
    static async getProjectPrefix(): Promise<void> {
        const archipadApp = document.querySelector('archipad-app') as any;
        const projectManager = archipadApp.project as ProjectManager | null;

        if (projectManager === null) {
            throw new IllegalStateError('Cannot retrieve the project prefix outside of a project');
        }

        const projectInfo = await getProjectInfo(null, projectManager.getId()).toPromise();
        const projectPrefix = `${projectInfo.patches.bucket}/${projectInfo.patches.path}`;

        console.log(`Project prefix: %c${projectPrefix}`, 'color: blue;');
        console.table({
            'Region': projectInfo.patches.region,
            'Bucket': projectInfo.patches.bucket,
            'Path': projectInfo.patches.path,
        });
    }

    /**
     * Create a Phase_BugGroups entity to associate a Phase to a BugGroup.
     * 
     * @param bugGroup - The BugGroup to associate to the Phase
     * @param phase - The Phase to associate to the BugGroup
     * @internal
     */
     static async createPhaseRelation(bugGroup: BugGroupEntity | null, phase: PhaseEntity): Promise<void> {
        const project = await Commands.getProject();
        const entityContext = project.getContext();
        const relationTemplateId = generateDeterministicTemplateIdFromTemplateIdAndTemplateId(phase.templateId, bugGroup?.templateId ?? '6485251F3220412089A38D3E28701777');
        const relationEntityId = generateDeterministicId(project.projectId, relationTemplateId, 'uuid');
        const relation = entityContext.getEntity('Phase_BugGroups', relationEntityId) ?? entityContext.createEntity('Phase_BugGroups', relationEntityId);
        relation.templateId = relationTemplateId;
        relation.phase = phase;
        relation.bugGroup = bugGroup;
    }

    /**
     * @internal
     * Return the current project entity
     */
    static async getProject(): Promise<ProjectEntity> {
        const entityContext = await Commands.getEntityContext();
        const projects = Object.values(entityContext.entitiesByType.Project) as ProjectEntity[];
        return projects[0];
    }

    /**
     * Set a list of templates which the project must depend on.
     * The content of each template will be added to the project.
     *
     * To apply changes to the project we must reload the page.
     *
     * Example:
     * ```
     * > Archipad.Commands.updateOriginTemplates(['831DA8A6A0F9AA28E3F9C0CAD530ABAD'])
     * Promise {<fulfilled>: undefined}
     * ```
     * @param {string[]} originTemplateIds List of templateId to apply to the project
     */
    static async updateOriginTemplates(originTemplateIds: string[]): Promise<void> {
        const re = /^[0-9A-F]{32}$/i;
        const isValid = originTemplateIds.every((templateId) => {
            if (!re.test(templateId)) {
                console.error(`Invalid template id: ${templateId}`);
                return false;
            }
            return true;
        })
        if (!isValid) {
            return;
        }

        const project = await Commands.getProject();
        project.originTemplateIds = originTemplateIds.join(',');

        const archipadApp = document.querySelector('archipad-app');
        const projectManager = (archipadApp as unknown as any).project as ProjectManager | null;
        const projectContext = await projectManager.getContext(null).toPromise();
        await projectManager.upgradeProjectUsingContext(null, projectContext).toPromise();
    }

    /**
     * Utility to make a new patch id.
     * 
     * @internal
     */
    static async makeNewPatchId(): Promise<{patchId: string}> {
        const patchId = await newPatchId(voidAbortSignal(), voidProgress());

        return { patchId };
    }

    /**
     * Utility to make a new template id.
     * 
     * @internal
     */
    static async makeNewTemplateId(): Promise<{templateId: string}> {
        const templateId = makeUUID();

        return { templateId };
    }

    /**
     * @internal
     */
    static decimalStringToUuid(decimalString: string): { uuid: string } {
        const hex = BigInt(decimalString).toString(16).padStart(32, "0");
	    const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        return { uuid };
    }

    /**
     * @internal
     */
    static uuidToDecimalString(uuid: string): { decimalString: string } {
        const hex = uuid.replace(/-/g, "");
	    const decimalString = BigInt(`0x${hex}`).toString();
        return { decimalString };
    }

    static async checkAllAttachments() {
        async function fetchAttachmentByEntities(entities: BaseEntity[]): Promise<string[]>{
        const attachmentsToPrefetch = [
            entities.flatMap(annotation => Object.values(annotation?.getAttachments() ?? {})),
        ].flat().filter(isNotNullish);
        const concurrencyLimit = pLimit(100);
        const totalLoadedFiles = [];
        const fetchAttachments$ = attachmentsToPrefetch.map(async (attachment, index) => 
            concurrencyLimit(async () => {
                if (attachment.type === 'localCache'){
                    // local cache attachments are already cached, no need to fetch them
                    return;
                }
                try {
                    const head = await firstValueFrom(FileService.metadata(attachment));
                    totalLoadedFiles.push(head.contentLength);
                } catch (error) {
                    console.error(`❌ Failed to prefetch attachment ${attachment} for project`, error);
                }
            }));
        await Promise.all(fetchAttachments$)
        return totalLoadedFiles;

        }

        function getAttachmentSize(entities: BaseEntity[]): number {
            return [
                entities.flatMap(annotation => Object.values(annotation?.getAttachments() ?? {})),
            ].flat().filter(isNotNullish).length;

        }
        const projectEntity = await Commands.getProject();
        const projectId = projectEntity.id;
        const maps = projectEntity.maps.map(map => map.activeVersion);
        const documents = projectEntity.documents.map(document => document.activeVersion);
        const bugNotesAnnotations = projectEntity.bugs.flatMap(bug => bug.notes).flatMap(note => [note, ...note.annotations]);
        const bugAnnotations = projectEntity.bugs.flatMap(bug => [bug, ...bug.annotations]);
        const remarksAnnotations = projectEntity.remarks.flatMap(remark => [remark, ...remark.annotations]);
        const reports = Array.from(projectEntity.reports);
        const attachments = {
            bugNotesAnnotations,
            bugAnnotations,
            remarksAnnotations,
            documents,
            maps,
            project: projectEntity,
            reports,
        }

        const bugsHead = await fetchAttachmentByEntities(attachments.bugAnnotations);
        const bugsTotal = bugsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);        
        console.log(`bugsHead complete`);
        const reportsHead = await fetchAttachmentByEntities(attachments.reports);
        const reportsTotal = reportsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`reportsHead complete`);
        const bugsNoteHead = await fetchAttachmentByEntities(attachments.bugNotesAnnotations);
        const bugsNoteTotal = bugsNoteHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`bugsNoteHead complete`);
        const remarksHead = await fetchAttachmentByEntities(attachments.remarksAnnotations);
        const remarksTotal = remarksHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`remarksHead complete`);
        const documentsHead = await fetchAttachmentByEntities(attachments.documents);
        const documentsTotal = documentsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`documentsHead complete`);
        const mapsHead = await fetchAttachmentByEntities(attachments.maps);
        const mapsTotal = mapsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`mapsHead complete`);
        const totalAttachmentsSize = bugsTotal + reportsTotal + bugsNoteTotal + remarksTotal + documentsTotal + mapsTotal;
        console.log(
            'All attachments have been processed',{
            projectId,
            totalAttachmentsSize: `${totalAttachmentsSize.toFixed(2)}`,
            totalLength: attachments.bugAnnotations.length + attachments.reports.length + attachments.bugNotesAnnotations.length + attachments.remarksAnnotations.length + attachments.documents.length + attachments.maps.length,
            bugsTotal: `${bugsTotal.toFixed(2)}`,
            bugsLength: attachments.bugAnnotations.length,
            reportsTotal: `${reportsTotal.toFixed(2)}`,
            reportsLength: attachments.reports.length,
            bugsNoteTotal: `${bugsNoteTotal.toFixed(2)}`,
            bugsNoteLength: attachments.bugNotesAnnotations.length,
            remarksTotal: `${remarksTotal.toFixed(2)}`,
            remarksLength: attachments.remarksAnnotations.length,
            documentsTotal: `${documentsTotal.toFixed(2)}`,
            documentsLength: attachments.documents.length,
            mapsTotal: `${mapsTotal.toFixed(2)}`,
            mapsLength: attachments.maps.length,
        });

    }

    static convertBytesToGo(bytes: number): number {
        return bytes / (1024 * 1024 * 1024);
    }

    static async diagnoseProject(): Promise<void> {
        console.log('Starting project diagnosis...');

        console.log('Checking missing bugNotes');
        await Commands.fixBugNotes();

        console.log('Checking for duplicated templatesIds...');
        const project = await Commands.getProject();
        const templateIdMap = new Map<string, BaseEntity[]>();
        Object.values(project.getContext().entitiesByType).forEach(entities => {
            Object.values(entities).forEach(entity => {
                if ('templateId' in entity && entity.templateId && typeof entity.templateId === 'string') {
                    const templateId = entity.templateId?.toLocaleLowerCase();
                    if (!templateIdMap.has(templateId)) {
                        templateIdMap.set(templateId, []);
                    }
                    templateIdMap.get(templateId)?.push(entity);
                }
            });
        });
        const duplicatedTemplateIds = Array.from(templateIdMap.entries()).filter(([_, entities]) => entities.length > 1);
        if (duplicatedTemplateIds.length > 0) {
            console.error('❌ Found duplicated templateIds:');
            duplicatedTemplateIds.forEach(([templateId, entities]) => {
                entities.slice(1).forEach(entity => {
                    entity['templateId'] = null;
                });
            });
        } else {
            console.log('✅ No duplicated templateIds found.');
        }

        console.log('Checking for duplicated mapNames...');
        const mapNameMap = new Map<string, MapEntity[]>();
        project.maps.forEach(map => {
            if (map.filename) {
                const mapName = map.filename;
                if (!mapNameMap.has(mapName)) {
                    mapNameMap.set(mapName, []);
                }
                mapNameMap.get(mapName)?.push(map);
            }
        });
        const duplicatedMapNames = Array.from(mapNameMap.entries()).filter(([_, maps]) => maps.length > 1);
        if (duplicatedMapNames.length > 0) {
            console.error('❌ Found duplicated map names:');
            duplicatedMapNames.forEach(([mapName, maps]) => {
                console.error(`Map Name: ${mapName}`);
                maps.forEach(map => {
                    console.error(`- MapId: ${map.id} Issues :${map.bugs.length}`);
                });
                const emptyMaps = maps.filter(map => map.bugs.length === 0);
                if (emptyMaps.length < maps.length - 1) {
                    console.error(`Only ${emptyMaps.length} maps are empty out of ${maps.length} maps with the same name, no map will be deleted to avoid data loss.`);
                } else {
                    emptyMaps.slice(maps.length - 1 ).forEach(map => {
                        console.error(`Deleting map ${map.id} with name ${mapName} because it has no bugs attached`);
                        project.getContext().deleteEntity(map);
                    });
                    console.log(`✅ Deleted ${emptyMaps.length - (maps.length - 1)} duplicated maps with name ${mapName} because they had no bugs attached`);
                }
                
            });
        } else {
            console.log('✅ No duplicated map names found.');
        }


        console.log('Checking maps file invalid... attachments...');
        const maps = project.maps;
        for (const map of maps) {
            if (map.fileType === MAP_FILE_TYPES.PDF && map.type === MAP_TYPES.DEFAULT) {
                const attachments = map.getAttachments();
                const keys = Object.keys(attachments);
                if (!keys.includes(map.filename)){
                    console.error(`❌ Map ${map.id} has an invalid file attachment, expected key: ${map.filename}, actual keys: ${keys.join(', ')}`);  
                    await Commands.fixMapAttachments(map.id);
                    console.log(`✅ Map ${map.id} attachment has been fixed, new keys: ${Object.keys(map.getAttachments()).join(', ')}`);
                }
            }
        }
        console.log('Checking attachments...');
        await Commands.checkAllAttachments();
    }

    static async fixMapAttachments(mapId: string): Promise<void> {
        const projectEntity = await Commands.getProject();
        const map = projectEntity.getContext().getEntity('Map', mapId);
        if (!map) {
            console.error(`❌ Unable to find the Map<${mapId}>`);
            return;
        }
        if (map.fileType !== MAP_FILE_TYPES.PDF || map.type !== MAP_TYPES.DEFAULT) {
            console.error(`❌ Map<${mapId}> is not of type PDF/DEFAULT, skipping attachment fix`);
            return;
        }
        const attachments = map.getAttachments();
        const keys = Object.keys(attachments);
        if (keys.includes(map.filename)){
            console.log(`✅ Map ${map.id} already has a valid attachment, no need to fix anything`);  
            return;
        }
        console.error(`❌ Map ${map.id} has an invalid file attachment, expected key: ${map.filename}, actual keys: ${keys.join(', ')}, fixing it now...`);  
        const mapVersion = map.activeVersion;
        if (!mapVersion) {
            console.error(`❌ Map ${map.id} does not have an active version, cannot fix attachment`);
            return;
        }
        const attachment = Object.values(mapVersion.getAttachments())[0];
        if (!attachment) {
            console.error(`❌ Map ${map.id} does not have any attachment on its active version, cannot fix attachment`);
            return;
        }
        map.setAttachmentForKey(map.filename, attachment);
        console.log(`✅ Map ${map.id} attachment has been fixed, new keys: ${Object.keys(map.getAttachments()).join(', ')}`);
    }

    static async fixBugNotes(): Promise<void> {
        const projectEntity = await Commands.getProject();
        const workflowService = new WorkflowService();
        const bugNotesWithoutState = projectEntity.bugs.filter(bug => !bug.stateNoteCache);
        if (bugNotesWithoutState.length === 0) {
            console.log('✅ All bug notes have a state, no need to fix anything');
            return;
        } else {
            console.error(`❌ There are ${bugNotesWithoutState.length} bugs without state note, fixing them now...`);
        }
        for (const bug of bugNotesWithoutState) {

            const bugNoteOpenId = generateDeterministicId(bug.bugId, "1AB0DC21BB2D4EC28B5DFDC82898AC89", "uuid");
            const bugNoteCloseId = generateDeterministicId(bug.bugId, "887329BD114949CCB2D0CBCC31B3F344", "uuid");

            let bugNoteOpen = bug.getContext().getEntity('BugNote', bugNoteOpenId);
            let bugNoteClose = bug.getContext().getEntity('BugNote', bugNoteCloseId);
            
            if (!bugNoteOpen) {
                bugNoteOpen = BugNoteFacadeInstance.createBugNoteModelPrimitive(bug, bugNoteOpenId);
                bugNoteOpen.creationDate = bug.openDate;
                bugNoteOpen.text = null;
                bugNoteOpen.visit = bug.visit;
                bugNoteOpen.date = bug.openDate || bug.visit?.date;
                bugNoteOpen.user = null;
            }

            if (bugNoteOpen) {
                bugNoteOpen.bugState = workflowService.findEquivBugState(EquivBugState.EquivStateOpen, projectEntity);
                bugNoteOpen.bugTransition = workflowService.findEquivTransition(EquivBugTransition.EquivTransitionEnter, bugNoteOpen.bugState, projectEntity)
            } 

            if (bugNoteClose) {
                bugNoteClose.bugState = workflowService.findEquivBugState(EquivBugState.EquivStateClosed, projectEntity);
                bugNoteClose.bugTransition = workflowService.findEquivTransition(EquivBugTransition.EquivTransitionAccept, bugNoteClose.bugState, projectEntity)
            }
        }
    }

	static async removeInvalidTmpAttachments(): Promise<void> {
        const entityContext = await Commands.getEntityContext();
        const updatedAttachmentKeys = entityContext.updatedAttachments;
        const createdAttachmentKeys = entityContext.createdAttachments;
        const AttachmentOperations = {
            created: createdAttachmentKeys,
            updated: updatedAttachmentKeys,
        };
        for (const [context, attachmentOperations] of Object.entries(AttachmentOperations)) {
            for (const [entityName, operations] of Object.entries(attachmentOperations)) {
                for (const [entityId, keys] of Object.entries(operations)) {
                    for (const [key, oldValue] of Object.entries(keys)) {
                        const entity = entityContext.getEntity(entityName, entityId);
                        if (!entity) {
                            console.warn(`Entity ${entityName} with id ${entityId} not found`);
                            continue;
                        }
                        const attachment = entity.getAttachmentForKey(key);
                        if (!attachment) {
                            console.warn(`Attachment for key ${key} not found on entity ${entityName} with id ${entityId}`);
                            continue;
                        }
                        try {
                            await firstValueFrom(FileService.metadata(attachment));
                        } catch (error) {
                            console.log(`Find missing attachments for entity ${entityName} with id ${entityId} and key ${key}, context: ${context}`);
                            if (context === 'created') {
                                entityContext.deleteEntity(entity);
                                console.log(`Deleted entity ${entityName} with id ${entityId} because its attachment ${key} is invalid`);
                            } else if (context === 'updated') {
                                if (!oldValue) {
                                    console.warn(`No old value for attachment ${key} on entity ${entityName} with id ${entityId}, cannot restore`);
                                }
                                entity.setAttachmentForKey(key, oldValue);
                                console.log(`Restored attachment ${key} for entity ${entityName} with id ${entityId} to its previous value`);
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * This global injection is intentionally opaque because those
 * commands should be used from the browser console only.
 */
(window as any).Archipad = {...(window as any).Archipad, Commands};
